/**
 * Post-processing for the plain-text bodies the chat models return.
 *
 * The AI features ask for "five headlines" or "three teasers" and then treat
 * the reply as data, but a chat completion is prose: the model is free to
 * answer with a Markdown list, wrap items in quotes, bold them, or open with a
 * sentence of preamble before the list starts. gpt-5 does all of these more
 * readily than the models these features were written against, so a reply like
 *
 *   Here are five options:
 *   - **First headline**
 *   - "Second headline"
 *
 * used to reach the UI verbatim, dashes and all. The prompts now ask for bare
 * lines, and these helpers make the parse hold up when the model decorates the
 * reply anyway.
 */

/** A Markdown bullet or an ordered-list number at the start of a line. */
const LIST_MARKER = /^\s*(?:[-*•·+–—]|\d+[.)])\s+/;

/** A line that introduces the list rather than being part of it. */
const PREAMBLE = /:\s*$/;

/**
 * Remove the inline Markdown a model uses for emphasis, and the quotes it
 * wraps individual suggestions in. Emphasis is unwrapped in pairs so words
 * that legitimately contain `*` or `_` survive, and only quotes around the
 * whole string are dropped so quoted words inside a headline survive.
 *
 * @param text One line or paragraph of model output.
 */
export function stripInlineMarkdown(text: string): string {
  return (text || '')
    .replace(/\*\*(\S(?:.*?\S)?)\*\*/g, '$1')
    .replace(/__(\S(?:.*?\S)?)__/g, '$1')
    .replace(/\*(\S(?:.*?\S)?)\*/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .trim()
    .replace(/^["'“‘](.*)["'”’]$/s, '$1')
    .trim();
}

/**
 * The individual suggestions in a model reply, whatever shape it arrived in:
 * one per line, one per paragraph, a bulleted list, or a numbered list.
 *
 * When any line is bulleted the un-bulleted lines are dropped, which is what
 * removes a "Here are five options" opener and any closing offer to keep
 * working. Duplicates are collapsed because a model asked for five sometimes
 * repeats one.
 *
 * @param content The `message.content` of a chat completion.
 * @param limit Most suggestions to return; omit for all of them.
 */
export function parseAiListItems(content: string, limit?: number): string[] {
  const lines = (content || '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  const bulleted = lines.filter((line) => LIST_MARKER.test(line));
  const candidates = bulleted.length
    ? bulleted
    : lines.filter((line) => !PREAMBLE.test(line));

  const items = candidates
    .map((line) => stripInlineMarkdown(line.replace(LIST_MARKER, '')))
    .filter(Boolean);

  const unique = Array.from(new Set(items));

  return limit ? unique.slice(0, limit) : unique;
}

/**
 * Model prose turned into the HTML the editor inserts. Emphasis markers are
 * stripped rather than converted — the reply is meant to be plain prose — and
 * the blank lines between paragraphs become breaks so the inserted text keeps
 * its shape instead of collapsing into one run.
 *
 * @param content The `message.content` of a chat completion.
 */
export function aiProseToHtml(content: string): string {
  return (content || '')
    .split(/\n{2,}/)
    .map((paragraph) => stripInlineMarkdown(paragraph).replace(/\n/g, '<br>'))
    .filter(Boolean)
    .join('<br><br>');
}
